import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import {ChannelAuthorizationCallback} from 'pusher-js/types/src/core/auth/options';
import apiClient from '@/core/http/clients/apiClient';

let instance: Echo<'reverb'> | null = null;

const EchoService = () => {
    return {
        /**
         * Whether the Reverb connection is configured for this deployment.
         */
        isConfigured(): boolean {
            return !!import.meta.env.VITE_REVERB_APP_KEY;
        },

        /**
         * Get the shared Echo connection to the Reverb server, creating it on first use.
         * Private channels are authorized through the API with the current bearer token.
         *
         * @returns The Echo instance, or null when the deployment has no Reverb configuration.
         */
        connect(): Echo<'reverb'> | null {
            if (!this.isConfigured()) {
                return null;
            }

            if (!instance) {
                // Reverb lives on the API host: behind its TLS proxy in production,
                // directly on its default 8080 port over plain http (local dev).
                const apiUrl = new URL(import.meta.env.VITE_API_URL);
                const forceTLS = apiUrl.protocol === 'https:';
                const port = forceTLS ? 443 : 8080;

                instance = new Echo({
                    broadcaster: 'reverb',
                    Pusher,
                    key: import.meta.env.VITE_REVERB_APP_KEY,
                    wsHost: apiUrl.hostname,
                    wsPort: port,
                    wssPort: port,
                    forceTLS,
                    enabledTransports: ['ws', 'wss'],
                    authorizer: (channel: {name: string}) => ({
                        authorize: (socketId: string, callback: ChannelAuthorizationCallback) => {
                            apiClient
                                // Reverb-signed variant: the default /broadcasting/auth
                                // signs with the Pusher credentials while both coexist.
                                .post('/broadcasting/reverb/auth', {
                                    socket_id: socketId,
                                    channel_name: channel.name,
                                })
                                .then(response => callback(null, response.data))
                                .catch(error => callback(error instanceof Error ? error : new Error(String(error)), null));
                        },
                    }),
                });
            }

            return instance;
        },

        /**
         * Leave a channel if the connection exists.
         *
         * @param channel The channel name, without the `private-` prefix.
         */
        leave(channel: string): void {
            instance?.leave(channel);
        },
    };
};

export default EchoService();
